import { NgIf } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import {
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';
import { getProducts } from 'src/app/feed-advice/store/feed-advice.selectors';
// import { getProductsRequest } from 'src/app/feed-advice/store/actions/feed-advice.actions';
import { Product } from 'src/app/shared/models/shared.model';

import { setSelectedProductRequest } from '../../store/actions/product.actions';
import { getSelectedCountry, getSelectedProduct } from '../../store/selectors/product.selectors';

export interface ProductDetailDialogInputData extends AbstractFormDialogData {
  productId?: number;
}
export interface ProductDetailDialogResultData {
  product: Product;
}

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    TranslatePipe,
  ],
})
export class ProductDetailComponent
  extends AbstractFormDialog<ProductDetailDialogResultData>
  implements OnDestroy
{
  data = inject<ProductDetailDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<ProductDetailComponent, AbstractDialogResultData<ProductDetailDialogResultData>>
    >(MatDialogRef);
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);

  @Input() formGroup: FormGroup;

  pageTitle: string = this.locale.translate('create-product.title-create');
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);
  products$: Observable<Product[]> = this.store.select(getProducts);

  product: Product;
  subProduct: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();

    this.data.title = this.locale.translate('create-product.title-create');

    this.formGroup = new FormGroup({
      productName: new FormControl(null, {
        validators: [Validators.required],
        asyncValidators: [this.validateProductNameCode()],
        updateOn: 'blur',
      }),
      productCode: new FormControl(null, {
        validators: [Validators.required],
        asyncValidators: [this.validateProductNameCode()],
        updateOn: 'blur',
      }),
      phase: new FormControl(null),
      type: new FormControl(null, [Validators.required]),
    });

    // this.store.dispatch(getProductsRequest());
    const id = this.data.productId;
    if (id && id > 0) {
      this.store.dispatch(setSelectedProductRequest({ id }));
      this.subProduct = this.store
        .select(getSelectedProduct)
        .pipe(skipWhile((c) => c == null))
        .subscribe((product) => {
          this.product = product;
          this.pageTitle = `${this.locale.translate('create-product.title-edit')} ${product.name}`;
          this.formGroup.controls.productName.setValue(product.name);
          this.formGroup.controls.productCode.setValue(product.code);
          this.formGroup.controls.phase.setValue(product.phase);
          this.formGroup.controls.type.setValue(product.type);
        });
    }
  }

  ngOnDestroy(): void {
    if (this.subProduct) {
      this.subProduct.unsubscribe();
    }
  }

  validateProductNameCode() {
    return <AsyncValidatorFn>((control: FormControl) => {
      if (this.product?.code !== control.value) {
        return this.products$.pipe(
          skipWhile((products) => products === null || products === undefined),
          take(1),
          map((products) => {
            if (
              products.some(
                (p) =>
                  p.name === this.formGroup.controls.productName.value &&
                  p.code === this.formGroup.controls.productCode.value,
              )
            ) {
              return { productCodeNameAlreadyInUse: true };
            }
          }),
        );
      }
      return of(null);
    });
  }

  save() {
    if (this.formGroup.valid) {
      this.selectedCountry$.subscribe((country) => {
        this.product = {
          ...this.product,
          name: <string>this.formGroup.controls.productName.value,
          code: <string>this.formGroup.controls.productCode.value,
          country,
          phase: <string>this.formGroup.controls.phase.value,
          isActive: true,
          animalType: 'Broiler',
          type: <string>this.formGroup.controls.type.value,
        };
        this.confirm({
          product: this.product,
        });
      });
    }
  }
}
